import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import productsApi, { Product, CreateProductRequest, UpdateProductRequest } from '@/services/apis/products';

// Query keys
export const productKeys = {
  all: ['products'] as const,
  lists: () => [...productKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...productKeys.lists(), filters] as const,
  details: () => [...productKeys.all, 'detail'] as const,
  detail: (id: string) => [...productKeys.details(), id] as const,
  byCategory: (category: string) => [...productKeys.all, 'category', category] as const,
};

export interface ProductsFilters {
  limit?: number;
  categoryId?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Hooks for fetching products with infinite scroll
export const useInfiniteProducts = (filters: ProductsFilters = {}) => {
  const { limit = 10, categoryId, search, sortBy = 'createdAt', sortOrder = 'desc' } = filters;

  return useInfiniteQuery({
    queryKey: productKeys.list({ limit, categoryId, search, sortBy, sortOrder }),
    queryFn: async ({ pageParam }) => {
      const response = await productsApi.getProducts({
        cursor: pageParam,
        limit,
        categoryId,
        search,
        sortBy,
        sortOrder,
      });
      return response;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo?.cursor || undefined;
    },
    initialPageParam: undefined as string | undefined,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useProduct = ({ id, shouldSkip }: { id: string; shouldSkip: boolean }) => {
  return useQuery({
    queryKey: productKeys.detail(id),
    queryFn: async () => {
      const response = await productsApi.getProduct(id);
      return response.data!;
    },
    enabled: !!id && !shouldSkip,
  });
};

// Hooks for mutations
export const useCreateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productData: CreateProductRequest) => productsApi.createProduct(productData),
    onSuccess: () => {
      // Invalidate and refetch products list
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
    },
  });
};

export const useUpdateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProductRequest }) => productsApi.updateProduct(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate specific product and products list
      queryClient.invalidateQueries({ queryKey: productKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
    },
  });
};

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => productsApi.deleteProduct(id),
    onSuccess: () => {
      // Invalidate products list
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
    },
  });
};
